import React, { useEffect, useState, useCallback } from 'react';
import { listCases, createCase, deleteCase } from './dprApi';
import { Card, Badge, PrimaryButton, GhostButton, Field, TextInput, SelectInput, CX_INPUT_STYLE } from './dprFields';
import CaseDetail from './CaseDetail';
import DprSidebar from './DprSidebar';
import { STEP_ICONS } from './dprStepIcons';

const Icon = {
  cases: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></svg>,
  draft: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4z" /></svg>,
  ready: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M9 11l3 3L22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" /></svg>,
  bank: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><line x1="3" y1="21" x2="21" y2="21" /><line x1="5" y1="21" x2="5" y2="10" /><line x1="19" y1="21" x2="19" y2="10" /><line x1="12" y1="21" x2="12" y2="10" /><polygon points="12 3 21 8 3 8" /></svg>,
  plus: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>,
  spark: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" /></svg>,
  search: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>,
  bell: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 0 1-3.46 0" /></svg>,
  chevronRight: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="9 18 15 12 9 6" /></svg>,
  shieldAlert: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /><line x1="12" y1="8" x2="12" y2="13" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>,
  bulb: <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M9 18h6M10 22h4M12 2a7 7 0 0 0-4 12.7V17h8v-2.3A7 7 0 0 0 12 2z" /></svg>,
  fileText: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></svg>,
  landmark: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="3" y1="21" x2="21" y2="21" /><line x1="5" y1="21" x2="5" y2="10" /><line x1="19" y1="21" x2="19" y2="10" /><line x1="12" y1="21" x2="12" y2="10" /><polygon points="12 3 21 8 3 8" /></svg>,
  arrowRight: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg>,
};

// Modal fade/scale-in — matches the reference mockup's entrance animation.
const MODAL_ANIM = `@keyframes cxModalIn { from { opacity: 0; transform: translateY(12px) scale(0.98); } to { opacity: 1; transform: translateY(0) scale(1); } }`;

function TypeCard({ selected, onClick, title, desc, icon, badge }) {
  return (
    <button type="button" onClick={onClick}
      className={`text-left p-4 rounded-xl border-[1.5px] flex flex-col gap-2 transition-all ${selected ? 'border-[#ff5200] shadow-[0_0_0_1px_#ff5200]' : 'border-slate-200 dark:border-slate-700 hover:border-slate-300'}`}
      style={selected ? { background: '#fff5f0' } : undefined}>
      <div className="flex items-center justify-between">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
          style={selected ? { background: '#ff5200', color: '#fff' } : { background: '#f0f3fa', color: '#001254' }}>
          {icon}
        </div>
        {badge && (
          <span className="text-[9.5px] font-extrabold uppercase px-1.5 py-0.5 rounded bg-white border" style={{ color: '#ff5200', borderColor: 'rgba(255,82,0,0.25)' }}>{badge}</span>
        )}
      </div>
      <div className="text-sm font-bold" style={{ color: '#001254' }}>{title}</div>
      <p className="text-[11.5px] text-slate-500 dark:text-slate-400 leading-relaxed">{desc}</p>
    </button>
  );
}

function NewCaseModal({ onClose, onCreated }) {
  const [projectTitle, setProjectTitle] = useState('');
  const [applicantName, setApplicantName] = useState('');
  const [purpose, setPurpose] = useState('bank_loan');
  const [mode, setMode] = useState('project');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const submit = async () => {
    if (!projectTitle || !applicantName) { setError('Project title and applicant name are required'); return; }
    setSaving(true); setError(null);
    try {
      const created = await createCase({ project_title: projectTitle, applicant_name: applicantName, report_purpose: mode === 'cma_only' ? 'cma_renewal' : purpose, case_mode: mode });
      onCreated(created.id);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,18,84,0.45)', backdropFilter: 'blur(4px)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <style>{MODAL_ANIM}</style>
      <div className="w-full max-w-[580px] bg-white dark:bg-slate-800 rounded-[20px] p-[30px] shadow-2xl" style={{ animation: 'cxModalIn 0.2s cubic-bezier(0.2,0.6,0.3,1)' }}>
        <h3 className="text-xl font-extrabold flex items-center gap-2" style={{ color: '#001254' }}>
          <span style={{ color: '#ff5200' }}>{Icon.spark}</span>
          <span className="dark:text-slate-100">New Project</span>
        </h3>
        <p className="text-[13px] text-slate-500 dark:text-slate-400 mt-1 leading-relaxed">A DPR is the complete project report; CMA data includes bank-facing financials only — pick what your client requires.</p>

        <div className="grid grid-cols-2 gap-3 my-[22px]">
          <TypeCard selected={mode === 'project'} onClick={() => setMode('project')} title="Full DPR" badge="Standard"
            desc="Build up figures from capacity, raw material & assumptions, plus an AI-drafted narrative report."
            icon={Icon.fileText} />
          <TypeCard selected={mode === 'cma_only'} onClick={() => setMode('cma_only')} title="CMA Data Only"
            desc="Existing business or WC renewal. Input Actual / Estimated / Projected financials directly."
            icon={Icon.landmark} />
        </div>

        <Field label="Project Title" required><TextInput value={projectTitle} onChange={setProjectTitle} placeholder={mode === 'cma_only' ? 'e.g. CMA for Working Capital Renewal' : 'e.g. Multigrain Flour Mill Unit'} /></Field>
        <Field label="Applicant / Entity Name" required><TextInput value={applicantName} onChange={setApplicantName} placeholder="e.g. Padmaja Rasooi Private Limited" /></Field>
        {mode === 'project' && (
          <Field label="Report Purpose">
            <SelectInput value={purpose} onChange={setPurpose} options={[
              { value: 'bank_loan', label: 'Bank Loan / Term Debt Sanction' }, { value: 'subsidy', label: 'Central / State Govt Capital Subsidy' },
              { value: 'cma_renewal', label: 'Working Capital Assessment (CC / OD)' }, { value: 'other', label: 'Private Equity / Investor Underwriting' },
            ]} />
          </Field>
        )}
        {error && <p className="text-xs text-rose-500 mb-3">{error}</p>}

        <div className="grid mt-6 pt-[18px] border-t border-slate-100 dark:border-slate-700" style={{ gridTemplateColumns: '1fr 1.6fr', gap: 10 }}>
          <GhostButton onClick={onClose}>Cancel</GhostButton>
          <PrimaryButton onClick={submit} disabled={saving}>
            <span className="flex items-center justify-center gap-2">{saving ? 'Creating…' : 'Create Project'} {!saving && Icon.arrowRight}</span>
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
}

function statusTone(status) {
  if (status === 'Finalized') return 'emerald';
  if (status === 'In Review') return 'amber';
  return 'slate';
}

function CaseList({ cases, onOpen, onDelete }) {
  if (!cases.length) {
    return (
      <div className="text-center py-16 text-sm text-slate-400">
        No projects yet. Click <span className="font-semibold text-slate-500">+ New Project</span> to build a report for a client.
      </div>
    );
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr>
            <th className="text-left px-5 py-2.5 border-b border-slate-100 dark:border-slate-800 font-bold text-slate-400 text-[11px] uppercase tracking-wide">Project</th>
            <th className="text-left px-5 py-2.5 border-b border-slate-100 dark:border-slate-800 font-bold text-slate-400 text-[11px] uppercase tracking-wide">Applicant</th>
            <th className="text-left px-5 py-2.5 border-b border-slate-100 dark:border-slate-800 font-bold text-slate-400 text-[11px] uppercase tracking-wide">Purpose</th>
            <th className="text-left px-5 py-2.5 border-b border-slate-100 dark:border-slate-800 font-bold text-slate-400 text-[11px] uppercase tracking-wide">Status</th>
            <th className="text-left px-5 py-2.5 border-b border-slate-100 dark:border-slate-800 font-bold text-slate-400 text-[11px] uppercase tracking-wide">Updated</th>
            <th className="border-b border-slate-100 dark:border-slate-800"></th>
          </tr>
        </thead>
        <tbody>
          {cases.map(c => (
            <tr key={c.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/60 cursor-pointer transition-colors" onClick={() => onOpen(c.id)}>
              <td className="px-5 py-3 border-b border-slate-50 dark:border-slate-800 font-bold text-slate-800 dark:text-slate-100">
                {c.project_title} {c.case_mode === 'cma_only' && <Badge tone="slate">CMA Only</Badge>}
              </td>
              <td className="px-5 py-3 border-b border-slate-50 dark:border-slate-800 text-slate-600 dark:text-slate-300">{c.applicant_name}</td>
              <td className="px-5 py-3 border-b border-slate-50 dark:border-slate-800 text-slate-500 capitalize">{(c.report_purpose || '').replace('_', ' ')}</td>
              <td className="px-5 py-3 border-b border-slate-50 dark:border-slate-800"><Badge tone={statusTone(c.status)}>{c.status}</Badge></td>
              <td className="px-5 py-3 border-b border-slate-50 dark:border-slate-800 text-slate-400 text-xs">{new Date(c.updated_at).toLocaleDateString()}</td>
              <td className="px-5 py-3 border-b border-slate-50 dark:border-slate-800 text-right">
                <button onClick={e => { e.stopPropagation(); onDelete(c.id); }} className="text-xs text-rose-500 hover:underline">Delete</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const TIPS = [
  { title: 'DSCR ≥ 1.25x', body: 'Banks treat 1.25x as the universal minimum for term loans — it gives a 25% cushion on debt service. Below 1.0x is an automatic rejection.' },
  { title: 'Current Ratio ≥ 1.33x', body: 'For manufacturing, this follows directly from the Tandon Committee\'s 25% promoter margin rule on working capital.' },
  { title: 'MPBF ≠ your full ask', body: 'MPBF (Method II) = 75% × (Current Assets − Current Liabilities). The bank funds the gap — it never replaces the promoter\'s own margin.' },
  { title: 'CMA vs DPR', body: 'CMA data answers "can you repay?" with standardized financial forms. A DPR answers "will this work?" with market and technical narrative. Banks often want both.' },
];

export default function DPRWorkspace() {
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openCaseId, setOpenCaseId] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [pinned, setPinned] = useState(() => localStorage.getItem('dpr-sidebar-pinned') === '1');
  const [caseNav, setCaseNav] = useState(null);
  const [search, setSearch] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    listCases().then(setCases).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const togglePinned = () => setPinned(p => {
    localStorage.setItem('dpr-sidebar-pinned', p ? '0' : '1');
    return !p;
  });

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this project? This cannot be undone.')) return;
    await deleteCase(id);
    load();
  };

  const goDashboard = () => { setOpenCaseId(null); setCaseNav(null); load(); };

  const draftCount = cases.filter(c => c.status === 'Draft').length;
  const reviewCount = cases.filter(c => c.status === 'In Review').length;
  const finalCount = cases.filter(c => c.status === 'Finalized').length;
  const filteredCases = search.trim()
    ? cases.filter(c => `${c.project_title} ${c.applicant_name}`.toLowerCase().includes(search.trim().toLowerCase()))
    : cases;

  return (
    <div className="flex h-screen overflow-hidden bg-[#f8fafc] dark:bg-slate-950">
      <style>{CX_INPUT_STYLE}</style>
      <DprSidebar pinned={pinned} onTogglePinned={togglePinned} onGoDashboard={goDashboard}
        atDashboard={!openCaseId} caseNav={openCaseId ? caseNav : null} stepIcons={STEP_ICONS} />

      {openCaseId ? (
        <div className="flex-1 min-w-0 overflow-y-auto">
          <CaseDetail caseId={openCaseId} onBack={goDashboard} registerNav={setCaseNav} />
        </div>
      ) : (
        <div className="flex-1 min-w-0 flex flex-col overflow-y-auto">
          {/* Top bar — search + notifications + user, matching the app-wide header pattern */}
          <header className="h-16 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 px-8 flex items-center justify-between sticky top-0 z-10 flex-shrink-0">
            <div className="relative w-96 max-w-full">
              <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400">{Icon.search}</span>
              <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search project name or applicant…"
                className="w-full pl-10 pr-4 py-2 bg-slate-50 dark:bg-slate-800 text-xs rounded-xl border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-[#0f1f45]/30 dark:focus:ring-orange-400/50 focus:bg-white dark:focus:bg-slate-800 transition" />
            </div>
            <div className="flex items-center gap-4">
              <button className="w-9 h-9 flex items-center justify-center rounded-xl text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 transition border border-slate-200 dark:border-slate-700">
                {Icon.bell}
              </button>
            </div>
          </header>

          <main className="p-8 max-w-7xl mx-auto w-full space-y-6">
            {/* Hero */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-100">Project Report Workspace</h1>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">Automated CMA preparation, DSCR calculations, and bank loan documentation.</p>
              </div>
              <div className="flex gap-3">
                <PrimaryButton variant="orange" onClick={() => setShowNew(true)}>
                  <span className="flex items-center gap-1.5">{Icon.plus} New Project</span>
                </PrimaryButton>
              </div>
            </div>

            {/* KPI cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className="p-4">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-slate-500 dark:text-slate-400">Total Projects</span>
                  <div className="w-8 h-8 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-600 dark:text-slate-300">{Icon.cases}</div>
                </div>
                <p className="text-2xl font-bold text-slate-900 dark:text-slate-100 mt-2">{cases.length}</p>
                <span className="text-[11px] text-slate-400 font-medium">Across DPR &amp; CMA-only cases</span>
              </Card>
              <Card className="p-4">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-slate-500 dark:text-slate-400">Drafts In Progress</span>
                  <div className="w-8 h-8 rounded-lg bg-amber-50 dark:bg-amber-500/10 flex items-center justify-center text-amber-600 dark:text-amber-300">{Icon.draft}</div>
                </div>
                <p className="text-2xl font-bold text-slate-900 dark:text-slate-100 mt-2">{draftCount}</p>
                <span className="text-[11px] text-amber-600 dark:text-amber-400 font-medium">Setup not yet complete</span>
              </Card>
              <Card className="p-4">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-slate-500 dark:text-slate-400">Under Review</span>
                  <div className="w-8 h-8 rounded-lg bg-sky-50 dark:bg-sky-500/10 flex items-center justify-center text-sky-600 dark:text-sky-300">{Icon.ready}</div>
                </div>
                <p className="text-2xl font-bold text-slate-900 dark:text-slate-100 mt-2">{reviewCount}</p>
                <span className="text-[11px] text-sky-600 dark:text-sky-400 font-medium">Internal review</span>
              </Card>
              <Card className="p-4">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-slate-500 dark:text-slate-400">Finalized</span>
                  <div className="w-8 h-8 rounded-lg bg-[#0f1f45]/[0.06] dark:bg-orange-500/10 flex items-center justify-center text-[#0f1f45] dark:text-orange-300">{Icon.bank}</div>
                </div>
                <p className="text-2xl font-bold text-slate-900 dark:text-slate-100 mt-2">{finalCount}</p>
                <span className="text-[11px] text-[#0f1f45] dark:text-orange-300 font-medium">Bank-ready reports</span>
              </Card>
            </div>

            {/* Table + side panel */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <Card className="lg:col-span-2 p-0 overflow-hidden flex flex-col">
                <div className="p-5 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between">
                  <div>
                    <h2 className="text-sm font-bold text-slate-900 dark:text-slate-100">Recent Projects</h2>
                    <p className="text-xs text-slate-400 mt-0.5">Manage and track your project report submissions</p>
                  </div>
                </div>
                {loading ? <div className="p-6 text-sm text-slate-400">Loading…</div> : <CaseList cases={filteredCases} onOpen={setOpenCaseId} onDelete={handleDelete} />}
              </Card>

              {/* Lending thresholds panel */}
              <Card className="p-6 flex flex-col justify-between">
                <div>
                  <div className="flex items-center justify-between pb-4 border-b border-slate-100 dark:border-slate-800">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-lg bg-[#0f1f45]/[0.06] dark:bg-orange-500/10 flex items-center justify-center text-[#0f1f45] dark:text-orange-300">{Icon.shieldAlert}</div>
                      <h2 className="font-bold text-sm text-slate-900 dark:text-slate-100">Bank Lending Thresholds</h2>
                    </div>
                    <Badge tone="brand">Rules</Badge>
                  </div>
                  <div className="mt-4 space-y-4">
                    {TIPS.map(t => (
                      <div key={t.title}>
                        <p className="text-xs font-bold text-[#0f1f45] dark:text-orange-300">{t.title}</p>
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 leading-relaxed">{t.body}</p>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="mt-6 pt-4 border-t border-slate-100 dark:border-slate-800 flex items-center gap-3">
                  <span className="text-amber-500 flex-shrink-0">{Icon.bulb}</span>
                  <p className="text-[11px] text-slate-500 dark:text-slate-400 leading-snug">
                    Sync CMA statements directly with schedule numbers to avoid lender audit re-queries.
                  </p>
                </div>
              </Card>
            </div>
          </main>
        </div>
      )}

      {showNew && (
        <NewCaseModal onClose={() => setShowNew(false)} onCreated={(id) => { setShowNew(false); load(); setOpenCaseId(id); }} />
      )}
    </div>
  );
}
